/**
 * MobileSwitchPopup — the "return to mobile?" banner (US port of the UK app's
 * constituencyprofiles MobileSwitchPopup). Rendered by App.js inside <Router>.
 *
 * The switch-to-desktop (the /m nav's "View desktop site") is otherwise ONE-WAY: it
 * sets preferDesktop=true and goes to /pol, and the entry-redirect then correctly
 * early-returns on preferDesktop — so a phone user would be stuck on desktop-on-phone.
 * This banner is the way back: shown ONLY to a phone-width user who switched away.
 *
 * DESKTOP SAFETY: gated behind isMobileDevice(), so a real desktop user (width ≥ 900)
 * NEVER sees it — the component renders null with no side-effect for desktop width.
 *
 * Show-condition (all must hold):
 *   • isMobileDevice()                         — phone width
 *   • localStorage.preferDesktop === "true"    — they chose desktop (else nothing to undo)
 *   • not dismissed this session               — sessionStorage flag (no per-page nag)
 *   • on a desktop content page                — not /m, the login form (/), /report-render, /admin
 */
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { isMobileDevice } from "./utils/mobileDetect";

const ACCENT = "#0f9fab";
const DISMISS_KEY = "mobileSwitchDismissed";

export default function MobileSwitchPopup() {
  const location = useLocation();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === "true"; } catch { return false; }
  });

  const path = location.pathname;
  const onDesktopPage =
    path !== "/" &&
    !path.startsWith("/m") &&
    !path.startsWith("/report-render") &&
    !path.startsWith("/admin");

  const shouldShow =
    !dismissed &&
    onDesktopPage &&
    (() => { try { return localStorage.getItem("preferDesktop") === "true"; } catch { return false; } })() &&
    isMobileDevice();

  if (!shouldShow) return null;

  const goMobile = () => {
    try { localStorage.removeItem("preferDesktop"); } catch { /* ignore */ }
    window.location.href = "/m"; // flag cleared → the entry-redirect works again
  };
  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, "true"); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Switch to mobile site"
      style={{
        position: "fixed",
        bottom: 0, left: 0, right: 0,
        zIndex: 9999,
        background: "#fff",
        borderTop: "1px solid rgba(0,0,0,0.10)",
        boxShadow: "0 -4px 24px rgba(0,0,0,0.13)",
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "14px 16px",
        paddingBottom: "calc(14px + env(safe-area-inset-bottom))",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 2 }}>
          Better on mobile
        </div>
        <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4 }}>
          Switch to the mobile version of this site.
        </div>
      </div>

      <button
        onClick={goMobile}
        style={{
          flexShrink: 0,
          background: ACCENT, color: "#fff", border: "none", borderRadius: 8,
          padding: "10px 18px", fontSize: 13, fontWeight: 700,
          cursor: "pointer", whiteSpace: "nowrap", WebkitTapHighlightColor: "transparent",
        }}
      >
        Switch
      </button>

      <button
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          flexShrink: 0, background: "none", border: "none", cursor: "pointer",
          color: "#9ca3af", padding: 8, display: "flex", alignItems: "center", justifyContent: "center",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}
